// @ts-nocheck
import { Suspense } from "react";
import type { PropsWithChildren } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import { TemplateCategory } from "@/components/confluence/templates/template-categories-search";
import { ContentNotFoundError } from "@/content/common/errors";
import {
    TemplateProvider,
    useCollections
} from "@/content/templates/template.provider";
import type {
    Template,
    TemplateCollection
} from "@/content/templates/template.types";
import ContentNotFoundErrorBoundary from "@/content/utils/content-not-found-error-boundary";
import { CollectionAboutSection } from "@/templates/software/confluence/templates/confluence-templates-page/src/sections/collection-about-section";
import { CollectionGuideSection } from "@/templates/software/confluence/templates/confluence-templates-page/src/sections/collection-guide-section";
import { CollectionRelatedSection } from "@/templates/software/confluence/templates/confluence-templates-page/src/sections/collection-related-section";
import { isValidConfluenceTemplateRoute } from "@/utils/is-valid-confluence-template";

import { CollectionHeaderSection } from "./sections/collection-header-section";
import { SharedHeaderFooterContainer } from "./sections/shared-header-footer-container";

export type ConfluenceTemplatesCollectionDetailsPageTemplateProps =
    PropsWithChildren<{
        templates?: Template[];
        collections?: TemplateCollection[];
        categories?: TemplateCategory[];
    }>;

const contentWrapper = css({
    "maxWidth": 1045,
    "margin": "0 auto",

    "@media (min-width: 30rem) and (max-width: 67.49rem)": {
        paddingInlineStart: 64,
        paddingInlineEnd: 64
    },
    [media.below.xs]: {
        paddingInlineStart: 32,
        paddingInlineEnd: 32
    }
});

const CollectionDetailsContent = () => {
    const collections = useCollections();

    const [collection, ...relatedCollections] = collections;

    if (
        !window.IS_STATIC &&
        !isValidConfluenceTemplateRoute(
            window.location.pathname,
            "collections",
            collections
        )
    )
        throw new ContentNotFoundError({
            message: "Could not find collection"
        });

    return (
        <div data-testid="confluence-template-collection-details-content">
            <CollectionHeaderSection collection={collection} />
            <div css={contentWrapper}>
                <CollectionAboutSection collection={collection} />
                {collection.howToUseThisCollection && (
                    <CollectionGuideSection
                        collectionGuide={collection.howToUseThisCollection}
                    />
                )}
                <CollectionRelatedSection collections={relatedCollections} />
            </div>
        </div>
    );
};

export const ConfluenceTemplatesCollectionDetailsPageTemplate = (
    props: ConfluenceTemplatesCollectionDetailsPageTemplateProps
) => {
    const { templates, collections, categories } = props;

    return (
        <SharedHeaderFooterContainer>
            <ContentNotFoundErrorBoundary>
                <TemplateProvider
                    templates={templates}
                    collections={collections}
                    categories={categories}
                >
                    <Suspense>
                        <CollectionDetailsContent />
                    </Suspense>
                </TemplateProvider>
            </ContentNotFoundErrorBoundary>
        </SharedHeaderFooterContainer>
    );
};
